import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhotoSizeSelectActualOutlined from '@mui/icons-material/PhotoSizeSelectActualOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import PropTypes from 'prop-types';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import ErrorMessage from 'src/components/common/Messages/ErrorMessage';
import Modal from 'src/components/common/Modal';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { actionPinnedFilesAtom } from 'src/components/planAction/PlanActionDetailPage/components/Action/atoms';
import { useTouchActionPlan } from 'src/components/planAction/PlanActionDetailPage/components/Action/hooks';

import FileDisplay from './components/FileDisplay';

import { styles } from './index.styles';

const accept =
  'video/*,image/*,.pdf,.docx,.doc,application/msword,.txt,.pptx,.ppt,.xlsx,.xls,.csv,.msg,.ics,.7z';

const fileKeyIndex = { current: 0 };
const getFileKey = () => fileKeyIndex.current++;

const Pinboard = ({ actionPlanId = null, actionId, editable = false }) => {
  const { t, i18n } = useTranslation(['planAction', 'common']);
  const [pinnedFiles, setPinnedFiles] = useAtom(
    actionPinnedFilesAtom(actionId),
  );
  const { changeCachedActionPlan } = usePlanActionContext();
  const { touchActionPlan } = useTouchActionPlan();
  const inputRef = useRef(null);
  const [highlight, setHighlight] = useState(false);

  const [error, setError] = useState('');

  const confirmUpload = useCallback(
    (key, fileData) => {
      setPinnedFiles((prev) =>
        prev.map((file) => (file.key !== key ? file : { key, ...fileData })),
      );
      touchActionPlan();
    },
    [setPinnedFiles, touchActionPlan],
  );

  const handleFileList = useCallback(
    (files) => {
      if (!files) return;
      setPinnedFiles((prev) => [
        ...prev,
        ...[...new Array(files.length || 0)].map((_, index) => {
          const key = getFileKey();
          const formData = new FormData();
          const file = files.item(index);
          formData.append('file', file);
          HttpServiceInstance.put(
            apiPaths.PLAN_ACTION.UPLOAD,
            formData,
            undefined,
            undefined,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          )
            .then(({ data }) => confirmUpload(key, data))
            .catch((axiosError) => {
              setPinnedFiles((old) => old.filter((entry) => entry.key !== key));
              if (
                axiosError.response &&
                axiosError.response.data &&
                axiosError.response.data[0] &&
                i18n.exists(
                  `planAction:ActionPlan.Errors.${axiosError.response.data[0]}`,
                )
              ) {
                setError(
                  t(
                    `planAction:ActionPlan.Errors.${axiosError.response.data[0]}`,
                  ),
                );
              } else {
                setError(`${axiosError}`);
              }
            });

          return {
            key,
            name: file.name.split('.')[0],
            extension: file.name.split('.')[1],
            deleting: false,
          };
        }),
      ]);
    },
    [setPinnedFiles, confirmUpload, i18n, t],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer && editable) {
        handleFileList(event.dataTransfer.files);
      }
      setHighlight(false);
    },
    [handleFileList, editable],
  );

  const handleDelete = useCallback(
    (id) => {
      setPinnedFiles((prev) =>
        prev.map((file) =>
          file.id !== id
            ? file
            : {
                ...file,
                deleting: true,
              },
        ),
      );
      HttpServiceInstance.delete(`${apiPaths.PLAN_ACTION.DELETE_FILE}${id}/`)
        .then(() => {
          setPinnedFiles((prev) => prev.filter((file) => file.id !== id));
          if (actionPlanId !== null) {
            changeCachedActionPlan(actionPlanId, (prev) => ({
              ...prev,
              actions: prev.actions.map((old) =>
                old.id !== actionId
                  ? old
                  : {
                      ...old,
                      attachments: old.attachments.filter(
                        (file) => file.id !== id,
                      ),
                    },
              ),
            }));
          }
        })
        .catch(() =>
          setPinnedFiles((prev) =>
            prev.map((file) =>
              file.id !== id ? file : { ...file, deleting: false },
            ),
          ),
        );
    },
    [setPinnedFiles, actionPlanId, actionId, changeCachedActionPlan],
  );

  return (
    <Stack direction="column" sx={{ p: 1.5, height: '100%' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('planAction:ActionPlan.YourPinBoard')}
      </Typography>
      <Stack
        direction="column"
        sx={styles.dropzone(highlight)}
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setHighlight(true);
        }}
        onDragLeave={() => setHighlight(false)}
      >
        <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
          {pinnedFiles.map((file) => (
            <FileDisplay
              file={file}
              key={file.key}
              onDelete={() => handleDelete(file.id)}
            />
          ))}
        </Stack>
        {editable && (
          <Stack direction="column" sx={styles.decoration}>
            <PhotoSizeSelectActualOutlined fontSize="large" />
            <div>
              <Button variant="cta" onClick={() => inputRef.current.click()}>
                {t('planAction:ActionPlan.DropHere')}
              </Button>
            </div>
            <Typography sx={styles.hint}>
              {t('planAction:ActionPlan.FileSizeHint')}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Box sx={styles.fileChooser}>
        <input
          id={`file-chooser-${actionId}`}
          data-test-id="file-chooser"
          value=""
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          onChange={(event) => handleFileList(event.target.files)}
        />
      </Box>
      <Modal
        onClose={() => setError('')}
        isOpen={!!error}
        headline={t('common:error')}
      >
        <ErrorMessage align="left">
          <p style={{ whiteSpace: 'pre-line' }}>{error}</p>
        </ErrorMessage>
      </Modal>
    </Stack>
  );
};

Pinboard.propTypes = {
  actionPlanId: PropTypes.number,
  actionId: PropTypes.number.isRequired,
  editable: PropTypes.bool,
};

export default Pinboard;
